/* __next_internal_client_entry_do_not_use__ PromoBanner auto */ import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER";
import { route as posHomeRoute } from "@faire/web-api--source/routes/point-of-sale";
import { route as posAboutRoute } from "@faire/web-api--source/routes/point-of-sale/about";
import compact from "lodash/compact";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo } from "react";
import { matchPath } from "react-router-dom";
import styled, { css } from "styled-components";
import { useZustandLocation } from "@faire/retailer-visitor-shared/app/_lib/routing/useZustandLocation";
import { AnimatedBanner } from "@faire/retailer/components/AnimatedBanner/AnimatedBanner";
import { PromoBannerType } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingNavSearchUX } from "@faire/retailer/settings/useSettingNavSearchUX";
import { useSettingPersonalizedFoiV2 } from "@faire/retailer/settings/useSettingPersonalizedFoiV2";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useRecordMilestoneByDay } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useLoggedOutPromoBanners } from "@faire/retailer/stores/ui/useLoggedOutPromoBanners";
import { usePromoBanners } from "@faire/retailer-visitor-shared/stores/ui/usePromoBanners";
import { getPromoBanner as getBanner } from "./__internal__/getPromoBanner";
import { MixedPromoBanners } from "./__internal__/MixedPromoBanners";
import { useSiteBannersList } from "./__internal__/useSiteBannersList";
var PromoBannerBase = function() {
    var personalizedFoiV2 = useSettingPersonalizedFoiV2();
    var siteBannersList = useSiteBannersList();
    var bannerRef = React.useRef(null);
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var isMobile = useViewport().isMobile;
    var _usePromoBanners = usePromoBanners(), bannerHeight = _usePromoBanners.bannerHeight, setBannerHeight = _usePromoBanners.setBannerHeight;
    var _useZustandLocation = useZustandLocation(function(state) {
        return state.pathname;
    }), pathnameWithMaybeLocale = _useZustandLocation.pathname;
    var hasLinkedPOS = usePOSSummary().hasLinkedPOS;
    var pathname = useMemo(function() {
        return removeLocaleFromUrl(pathnameWithMaybeLocale);
    }, [
        pathnameWithMaybeLocale
    ]);
    var nonStickyHeader = useMemo(function() {
        var nonStickyHeaderPath = [
            {
                path: posAboutRoute,
                exact: true
            }
        ].concat(_to_consumable_array(!hasLinkedPOS ? [
            {
                path: posHomeRoute,
                exact: true
            }
        ] : [])).filter(isNotUndefined);
        return nonStickyHeaderPath.some(function(path) {
            return matchPath(pathname, path) !== null;
        });
    }, [
        pathname,
        hasLinkedPOS
    ]);
    var _useMarketStore = useMarketStore([
        "isShowingCountdownBanner"
    ]), isShowingMarketCountdownBanner = _useMarketStore.isShowingCountdownBanner;
    useEffect(function() {
        if (bannerRef.current != null) {
            if (bannerHeight !== bannerRef.current.clientHeight) {
                setBannerHeight(bannerRef.current.clientHeight);
            }
        }
    });
    var recordMilestoneByDay = useRecordMilestoneByDay();
    var banners = usePromoBanners().banners;
    useEffect(function() {
        return function() {
            if (banners.includes(PromoBannerType.REFERRAL_FOLLOWUP)) {
                recordMilestoneByDay(HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER);
            }
        };
    }, [
        banners,
        recordMilestoneByDay
    ]);
    var loggedOutPromoBanners = useLoggedOutPromoBanners();
    var isBannerlessVariant = useSettingNavSearchUX().isBannerlessVariant;
    var getBannerWithKeyProp = useCallback(function(type, index) {
        var banner = getBanner(type);
        return banner ? /*#__PURE__*/ _jsx(React.Fragment, {
            children: getBanner(type)
        }, index) : null;
    }, []);
    var bannersWithKeyProp = useCallback(function(bannerList) {
        return compact(bannerList.map(getBannerWithKeyProp));
    }, [
        getBannerWithKeyProp
    ]);
    var renderBanners = useCallback(function() {
        var availableBanners = isLoggedInRetailer ? banners : loggedOutPromoBanners;
        if (availableBanners.length === 0) {
            return null;
        }
        if (!isLoggedInRetailer && isBannerlessVariant && isMobile && availableBanners[0] !== PromoBannerType.CONSUMER_MODE) {
            return null;
        }
        // Banners that do not need special styling
        if (availableBanners[0] === PromoBannerType.FAIRE_DIRECT_INCENTIVES || availableBanners[0] === PromoBannerType.FAIRE_VALUES && !personalizedFoiV2 || availableBanners[0] === PromoBannerType.MARKET_COUNTDOWN || availableBanners[0] === PromoBannerType.BRAND_PREVIEW || availableBanners[0] === PromoBannerType.CONSUMER_MODE) {
            return bannersWithKeyProp(availableBanners);
        }
        if (siteBannersList.length > 0 && personalizedFoiV2) {
            return /*#__PURE__*/ _jsx(MixedPromoBanners, {
                banners: siteBannersList
            });
        }
        return /*#__PURE__*/ _jsx(StyledAnimatedBanner, {
            banners: bannersWithKeyProp(availableBanners),
            bannerNames: availableBanners
        });
    }, [
        banners,
        bannersWithKeyProp,
        isBannerlessVariant,
        isLoggedInRetailer,
        isMobile,
        loggedOutPromoBanners,
        personalizedFoiV2,
        siteBannersList
    ]);
    return useMemo(function() {
        return /*#__PURE__*/ _jsx(BannerWrapper, {
            ref: bannerRef,
            $isSticky: !nonStickyHeader && isShowingMarketCountdownBanner,
            children: renderBanners()
        });
    }, [
        isShowingMarketCountdownBanner,
        nonStickyHeader,
        renderBanners
    ]);
};
export var PromoBanner = observer(PromoBannerBase);
var StyledAnimatedBanner = styled(AnimatedBanner).withConfig({
    componentId: "sc-eb5323f2-0"
})([
    "background-color:",
    ";"
], Decorative.tint.neutral);
var BannerWrapper = styled.div.withConfig({
    componentId: "sc-eb5323f2-1"
})([
    "z-index:2;position:relative;",
    ";"
], function(param) {
    var $isSticky = param.$isSticky;
    return $isSticky ? css([
        "",
        "{position:sticky;top:0;z-index:13;}"
    ], responsiveMediaQueries.desktopAndAbove) : null;
});
